class VariantPicker extends HTMLElement {
  constructor() {
    super();

    this.handleOptionChange = this.handleOptionChange.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.formId;
    this.formEl = document.forms[this.formId];
    this.inputs = this.querySelectorAll('input')

    this.inputs.forEach(async input => {
      const productUrl = input?.dataset?.productUrl
      const combinedProductUrl = input?.dataset?.combinedProductUrl

      //Replace product url for combined listings - this prevents going to the parent url
      if (!productUrl && combinedProductUrl) {
        const response = await fetch(`${combinedProductUrl}?view=json`)
        const product = await response.json()
        if (product) {
          const firstAvailableVariant = product?.variants?.find(variant => variant[input.name] === input.value)
          if (firstAvailableVariant && firstAvailableVariant?.product_handle) {
            const newUrl = `/products/${firstAvailableVariant.product_handle}?variant=${firstAvailableVariant.id}`
            if (newUrl) {
              input.setAttribute('data-product-url', newUrl)
            }
          }
        }
      }
    })

    this.addEventListener('change', this.handleOptionChange)
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleOptionChange);
  }

  async handleOptionChange(event) {
    const productElement = this.closest('#product');

    // Combined listings: We use old and new product urls to determine if the product should change
    const oldProductUrl = productElement.dataset.productUrl;
    const newProductUrl = event.target.dataset.productUrl;

    const url = `${newProductUrl ? newProductUrl : oldProductUrl}?section_id=${this.sectionId}`

    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      }
    });
    const responseText = await response.text();

    const html = new DOMParser().parseFromString(responseText, 'text/html');

    const newEmbroideryModals = html.querySelectorAll('modal-dialog[id*="EmbroideryPopup"]');

    const newPageTitleTagInnerText = html.querySelector('#product').dataset.pageTitleTagInnerText;
    if (newPageTitleTagInnerText && newPageTitleTagInnerText !== document.querySelector("title").innerText) {
      document.querySelector("title").innerText = newPageTitleTagInnerText;
    }

    // Check if the option change is a variant option change or a combined listings option change
    window.history.replaceState({}, '', `${newProductUrl}`);

    // This can be more fine tuned to only update certain HTML fragments in the section. -Michael
    document.querySelector(`#shopify-section-${this.sectionId}`).innerHTML = html.querySelector(`#shopify-section-${this.sectionId}`).innerHTML;

    const newProductId = document.querySelector('#product')?.dataset.productId;
    if (newProductId) {
      const mainSection = document.querySelector(`#shopify-section-${this.sectionId}`);
      document.querySelectorAll('product-recommendations').forEach(el => {
        if (!mainSection.contains(el) && newProductId !== el.dataset.productId) {
          el.dataset.productId = newProductId;
          el.loadRecommendations(newProductId);
        }
      });
    }

    // Clean up old embroidery modals from previous products
    const oldEmbroideryModals = document.querySelectorAll('modal-dialog[id*="EmbroideryPopup"]');
    oldEmbroideryModals.forEach(modal => {
      modal.remove();
    });

    // Add new embroidery modals from fetched HTML
    if (newEmbroideryModals.length > 0) {
      newEmbroideryModals.forEach(modal => {
        const clonedModal = modal.cloneNode(true);
        // Set the moved property to prevent the connectedCallback error
        clonedModal.moved = true;
        // Set the dataset section manually if needed
        clonedModal.dataset.section = this.sectionId;
        document.body.appendChild(clonedModal);
      });
    }

    // Reinitialize product form customization logic after dynamic HTML update
    this.initializeCustomizationCheck();

    const modalSlider = document.querySelector(`#Slider-ProductMediaModal-${this.sectionId}`);
    const newModalSlider = html.querySelector(`#Slider-ProductMediaModal-${this.sectionId}`);

    if (modalSlider && newModalSlider) {
      modalSlider.innerHTML = newModalSlider.innerHTML;
      modalSlider.closest('slider-component')?.resetPages();
    }
    
    // Focus the input for the last clicked option value
    document.querySelector(`#${event.target.id}`).focus();
    document.dispatchEvent(new CustomEvent('variant-updated'))
    return;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  get currentVariantId() {
    return document.querySelector('#product input[name="id"]').value;
  }

  initializeCustomizationCheck() {
    // Wait for custom elements to be fully initialized
    setTimeout(() => {
      const productForm = document.querySelector('product-form');
      
      if (productForm) {
        // Try to call the method if it exists
        if (typeof productForm.checkForCustomizedVariantOnLoad === 'function') {
          productForm.checkForCustomizedVariantOnLoad();
        } else {
          // Fallback: manually implement the customization check
          this.manualCustomizationCheck(productForm);
        }
      }
    }, 600); // Slightly longer delay to ensure initialization
  }

  manualCustomizationCheck(productForm) {
    // Replicate the logic from product-form.js performCustomizationCheck
    const isCustomized = this.isCustomizedVariant();
    const hasPersonalization = this.hasPersonalizationData();
    
    if (isCustomized && !hasPersonalization) {
      const submitButton = productForm.querySelector('[type="submit"]');
      const submitButtonText = submitButton?.querySelector('.product-form__add-button-text');
      
      // Show customization required message
      this.showCustomizationRequiredMessage(productForm);
      
      // Disable button and update text
      if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
        if (submitButtonText) {
          submitButtonText.textContent = 'Personalize to Add to Bag';
        }
      }
    } else if (!isCustomized) {
      // Clean up message if variant is not customized
      const existingMessage = document.querySelector('.customization-required-message');
      if (existingMessage) {
        existingMessage.remove();
      }
      
      const submitButton = productForm.querySelector('[type="submit"]');
      const submitButtonText = submitButton?.querySelector('.product-form__add-button-text');
      
      if (submitButton) {
        submitButton.removeAttribute('disabled');
        if (submitButtonText) {
          submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to Bag';
        }
      }
    }
  }

  hasPersonalizationData() {
    const textInput = document.querySelector('[name="properties[Embroidery Text]"]');
    const fontInput = document.querySelector('[name="properties[Embroidery Font]"]');
    const colorInput = document.querySelector('[name="properties[Embroidery Thread Color]"]');
    
    const hasEmbroideryText = textInput?.value?.trim();
    const hasEmbroideryFont = fontInput?.value;
    const hasEmbroideryColor = colorInput?.value;

    return hasEmbroideryText && hasEmbroideryFont && hasEmbroideryColor;
  }

  isCustomizedVariant() {
    // Primary check: Look for "Customized" option in variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const selectedOptions = variantPicker.querySelectorAll('input[type="radio"]:checked');
      const hasCustomizedOption = Array.from(selectedOptions).some(input => input.value === 'Customized');
      
      if (hasCustomizedOption) {
        return true;
      }
    }
    
    // Secondary check: Check if there's an embroidery modal/button for this product
    const embroideryButton = document.querySelector('.embroidery-popup-modal__button');
    if (embroideryButton) {
      return true;
    }
    
    return false;
  }

  showCustomizationRequiredMessage(productForm) {
    // Remove any existing message first
    const existingMessage = document.querySelector('.customization-required-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create new message
    const messageEl = document.createElement('div');
    messageEl.className = 'customization-required-message alert alert--warning';
    messageEl.innerHTML = `
      <p class="body-xs">This variant requires personalization. Please click "Personalize it" to add your embroidery details before adding to cart.</p>
    `;

    // Find the correct insertion point - look for product form container
    const productFormContainer = productForm.closest('.product-form') || productForm.closest('.product__content');
    const form = productForm.querySelector('form');
    const targetParent = productFormContainer || (form ? form.parentElement : productForm);
    
    // Insert before the form, not inside it
    if (targetParent && form) {
      targetParent.insertBefore(messageEl, form);
    } else {
      // Fallback: insert before the product form element
      const submitButton = productForm.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.parentElement.insertBefore(messageEl, submitButton);
      }
    }
  }
}

customElements.define('variant-picker', VariantPicker);
